/**
 * Card showing progress for a single analysis pipeline stage.
 *
 * Displays the stage name, completed/total count, a progress bar,
 * and a status icon.
 */

import { Activity, AlertCircle, CheckCircle2, Clock, SkipForward } from 'lucide-react';

export type StageCardStatus = 'pending' | 'running' | 'completed' | 'error' | 'skipped';

interface Props {
  name: string;
  status: StageCardStatus;
  completed: number;
  total: number;
}

const statusConfig: Record<
  StageCardStatus,
  { icon: React.ReactNode; barClass: string }
> = {
  pending: {
    icon: <Clock className="h-4 w-4 text-gray-400" />,
    barClass: 'bg-gray-300 dark:bg-gray-600',
  },
  running: {
    icon: <Activity className="h-4 w-4 text-blue-500 animate-pulse" />,
    barClass: 'bg-blue-500',
  },
  completed: {
    icon: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    barClass: 'bg-green-500',
  },
  error: {
    icon: <AlertCircle className="h-4 w-4 text-red-500" />,
    barClass: 'bg-red-500',
  },
  skipped: {
    icon: <SkipForward className="h-4 w-4 text-gray-400" />,
    barClass: 'bg-gray-300 dark:bg-gray-600',
  },
};

export function AnalysisStageCard({ name, status, completed, total }: Props) {
  const { icon, barClass } = statusConfig[status];
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="rounded-lg border border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {name}
        </span>
        {icon}
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-in-out ${barClass}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Footer */}
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          {completed} / {total}
        </span>
        <span>{pct}%</span>
      </div>
    </div>
  );
}
